import React, { useEffect, useState } from 'react';
import api from '../BaseURL/BaseURL';
import { useAuth } from '../Contexts/AuthContext';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const Home = () => {
  const { token } = useAuth();
  const [startDate, setStartDate] = useState(() => {
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);
    return lastWeek.toISOString().split('T')[0]; 
  });
  
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  
  const [shiftsData, setShiftsData] = useState(null);
  const [reasonsData, setReasonsData] = useState(null);
  const [sourcesData, setSourcesData] = useState(null);
  const [edibleData, setEdibleData] = useState(null);
  const [foodWasteSummary, setFoodWasteSummary] = useState(null);
  const [foodTypesData, setFoodTypesData] = useState(null);
  const [interactionData, setInteractionData] = useState(null);
  const [foodTypeCostLoss, setFoodTypeCostLoss] = useState([]);




  const fetchData = async () => {
    try {
      const response = await api.get('/institution/charts', {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        params: {
          start_date: startDate || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], 
          end_date: endDate || new Date().toISOString().split('T')[0], 
        }
      });

      if (response.data.success) {
        setShiftsData(response.data.data.shifts);
        setReasonsData(response.data.data.reasons); 
        setSourcesData(response.data.data.sources);
        setEdibleData(response.data.data.edible_percentages);
        setFoodWasteSummary(response.data.data.food_waste_summary);
        setFoodTypesData(response.data.data.food_types);
        setInteractionData(response.data.data.interaction_data);
        setFoodTypeCostLoss(response.data.data.food_type_cost_loss); 
      }
    } catch (error) {
      console.error('Error fetching waste data:', error);
    }
  };

  useEffect(() => {
    fetchData(); 
  }, []);

  
  const processShiftsChartData = () => {
    if (!shiftsData) return null;

    const labels = shiftsData.map(shift => shift.shift);
    const totalWaste = shiftsData.map(shift => shift.total_waste_grams);
    const avgWaste = shiftsData.map(shift => shift.avg_waste_grams);

    return {
      labels,
      datasets: [
        {
          label: 'Total',
          data: totalWaste,
          backgroundColor: '#ffa333',
          borderWidth: 1,
        },
        {
          label: 'Average',
          data: avgWaste,
          backgroundColor: '#cd93d7', 
          borderWidth: 1,
        }
      ],
    };
  };

 
  const processReasonsChartData = () => {
    if (!reasonsData) return null;

    const labels = reasonsData.map(reason => reason.reason_name);
    const totalWasteGrams = reasonsData.map(reason => reason.total_waste_grams);

    return {
      labels,
      datasets: [
        {
          label: 'Total',
          data: totalWasteGrams,
          backgroundColor: '#f0624c',
          borderWidth: 1,
        },
        
      ],
    };
  };

  const processSourcesChartData = () => {
    if (!sourcesData) return null;

    const labels = sourcesData.map(source => source.source_name);
    const totalWasteGrams = sourcesData.map(source => source.total_waste_grams);

    return {
      labels,
      datasets: [
        {
          label: 'Total',
          data: totalWasteGrams,
          backgroundColor: '#5eb5ef',
          borderWidth: 1,
        },
      ],
    };
  };

  const processEdibleChartData = () => {
    if (!edibleData) return null;

    return {
      labels: ['Edible Food Waste', 'Non-Edible Food Waste'],
      datasets: [
        {
          data: [edibleData.edible, edibleData.non_edible],
          backgroundColor: ['#4CAF50', '#8B4513'], 
          borderWidth: 1,
        }
      ],
    };
  };

  const processFoodTypesChartData = () => {
    if (!foodTypesData) return null;
  
    const labels = foodTypesData.map(food => food.food_type_name);
    const totalWasteGrams = foodTypesData.map(food => food.total_waste_grams);
  
    return {
      labels,
      datasets: [
        {
          label: 'Total',
          data: totalWasteGrams,
          backgroundColor: '#0acb8e',
          borderWidth: 1,
        },
      ],
    };
  };
  
  const sourceColors = [
    "#ff6384", // Pink
    "#36a2eb", // Blue
    "#ffce56", // Yellow
    "#4caf50", // Green
    "#9966ff", // Purple
    "#ff9f40", // Orange
    "#008000", // Dark Green
    "#800080", // Dark Purple
    "#0000FF", // Blue
    "#FF0000"  // Red
  ];
  
  // Function to get a color based on index
  const getColorForIndex = (index) => {
    return sourceColors[index % sourceColors.length]; // Loop through colors if needed
  };

  
  const processInteractionChartData = () => {
    if (!interactionData) return null;
  
    const labels = interactionData.map(reason => reason.reason_name);
    const datasets = [];
  
    // Get all unique sources (without hardcoding names)
    const allSources = new Set();
    interactionData.forEach(reason => {
      if (Array.isArray(reason.sources)) {
        reason.sources.forEach(source => allSources.add(source.source_name));
      }
    });
  
    // Convert set to array
    const sourcesArray = Array.from(allSources);
  
    sourcesArray.forEach((sourceName, index) => {
      datasets.push({
        label: sourceName,
        data: interactionData.map(reason => {
          const source = Array.isArray(reason.sources)
            ? reason.sources.find(src => src.source_name === sourceName)
            : null;
          return source ? source.total_waste_grams : 0;
        }),
        backgroundColor: getColorForIndex(index), // 🎨 Assign color based on index
        stack: 'Stack 0', // ✅ Stack all sources together
      });
    });
  
    return {
      labels,
      datasets,
    };
  };
  
  
  const foodTypeColors = [
    "#fec400"
  ];
  
  // Function to assign colors dynamically
  const getColorForIndex1 = (index) => foodTypeColors[index % foodTypeColors.length];

  
  const processFoodTypeCostLossChartData = () => {
    if (!foodTypeCostLoss || foodTypeCostLoss.length === 0) return null;
  
    const labels = foodTypeCostLoss.map(item => item.food_type_name); // Food type names
    const data = foodTypeCostLoss.map(item => item.total_cost_loss); // Cost loss values
  
    return {
      labels,
      datasets: [
        {
          label: "Cost Loss Per Food Type (JOD)",
          data,
          backgroundColor: foodTypeCostLoss.map((_, index) => getColorForIndex1(index)), // Assign colors dynamically
          borderWidth: 1,
        },
      ],
    };
  };
  
  
  return (
    <div   
    className="p-6 overflow-y-auto" 
    dir="rtl" 
    style={{ maxHeight: '75vh', scrollbarWidth: 'thin' }}
  >
      <h1 className="text-2xl font-semibold text-center text-gray-700 mb-6">الرئيسية</h1>

      <div className="flex flex-col md:flex-row justify-center items-center gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 text-right mb-2">من تاريخ</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:outline-none text-gray-700"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 text-right mb-2">إلى تاريخ</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:outline-none text-gray-700"
          />
        </div>
        <button
          onClick={fetchData}
          className="bg-[#DD5B35] text-white px-6 py-2 rounded-lg hover:bg-[#F4A68D] hover:text-black mt-4 md:mt-6"
        >
          فلترة البيانات
        </button>
      </div>


      {foodWasteSummary && (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
    {/* Total Waste (Grams) */}
    <div className="bg-white shadow-md p-4 rounded-lg flex flex-col items-center">
      <p className="text-xl font-semibold">{foodWasteSummary.total_waste_grams} G</p>
      <p className="text-sm text-gray-500">
        <span className="font-semibold">TOT</span>  TOTAL Food Waste | G 
      </p>
    </div>

    {/* Total Waste (Kg) */}
    <div className="bg-white shadow-md p-4 rounded-lg flex flex-col items-center">
      <p className="text-xl font-semibold">{foodWasteSummary.total_waste_kg} Kg</p>
      <p className="text-sm text-gray-500">
        <span className="font-semibold">TOT</span>  TOTAL Food Waste | KG 
      </p>
    </div>

    {/* Average Waste (Grams) */}
    <div className="bg-white shadow-md p-4 rounded-lg flex flex-col items-center">
      <p className="text-xl font-semibold">{foodWasteSummary.average_waste_grams} G</p>
      <p className="text-sm text-gray-500">
        <span className="font-semibold">AVERAGE</span> Food Waste | G
      </p>
    </div>

    {/* Average Waste (Kg) */}
    <div className="bg-white shadow-md p-4 rounded-lg flex flex-col items-center">
      <p className="text-xl font-semibold">{foodWasteSummary.average_waste_kg} Kg</p>
      <p className="text-sm text-gray-500">
        <span className="font-semibold">AVERAGE</span> Food Waste | KG
      </p>
    </div>
  </div>
)}


      {edibleData && (
  <div className="flex flex-col items-center mb-12">
    <h2 className="text-xl font-semibold text-center text-gray-600 mb-4">
    Proportion of Edible VS. lnedible Food Waste 
    </h2>
    <div style={{ width: '400px' }}>
    <Pie 
  data={processEdibleChartData()} 
  options={{
    responsive: true,
    plugins: {
      legend: {
        position: 'top', 
        align: 'center',
        labels: {
          usePointStyle: true, 
          boxWidth: 12, 
          padding: 20, 
        }
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const dataset = tooltipItem.dataset;
            const index = tooltipItem.dataIndex;
            return `${dataset.data[index]}%`;
          }
        }
      }
    }
  }}
/>

    </div>
  </div>
)}


      {shiftsData && (
        <div className="flex flex-col items-center mb-12">
          <h2 className="text-xl font-semibold text-center text-gray-600 mb-4">
          Total & Average Weight of Wasted Food by Work Shift
          </h2>
          <div style={{ width: '900px' }}>
            <Bar 
              data={processShiftsChartData()} 
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'top' },
                  tooltip: {
                    enabled: true,
                    callbacks: {
                      label: function (tooltipItem) {
                        const dataset = tooltipItem.dataset;
                        const index = tooltipItem.dataIndex;
                        const shift = shiftsData[index];

                        if (!shift) return dataset.label + ": " + dataset.data[index] + "g";

                        if (dataset.label === 'Total Food Waste') {
                          return `${dataset.label}: ${shift.total_waste_grams}g (${shift.total_waste_kg}kg)`;
                        } else {
                          return `${dataset.label}: ${shift.avg_waste_grams}g (${shift.avg_waste_kg}kg)`;
                        }
                      }
                    }
                  },
                },
                scales: {
                  x: { 
                    title: { display: true, text: "Work Shifts" },
                    ticks: { autoSkip: false } 
                },
                  y: { title: { display: true, text: "Weight of Wasted Food (g)" } }
                }
              }} 
            />
          </div>
        </div>
      )}

      {reasonsData && (
        <div className="flex flex-col items-center mb-12">
          <h2 className="text-xl font-semibold text-center text-gray-600 mb-4">
          Total Weight of Wasted Food by Reason (g)
          </h2>
          <div style={{ width: '900px' }}>
            <Bar 
              data={processReasonsChartData()} 
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'top' },
                  tooltip: {
                    enabled: true,
                    callbacks: {
                      label: function (tooltipItem) {
                        const dataset = tooltipItem.dataset;
                        const index = tooltipItem.dataIndex;
                        const reason = reasonsData[index];

                        if (!reason) return dataset.label + ": " + dataset.data[index];

                        return `${dataset.label}: ${reason.total_waste_grams}g (${reason.total_waste_kg}kg)`;
                      }
                    }
                  },
                },
                scales: {
                  x: { 
                  title: { display: true, text: "Reason for Food Waste" },
                  ticks: { autoSkip: false }
                },
                  y: { title: { display: true, text: "Total Weight of Wasted Food (g)" } }
                }
              }} 
            />
          </div>
        </div>
      )}

{sourcesData && (
  <div className="flex flex-col items-center mb-12">
    <h2 className="text-xl font-semibold text-center text-gray-600 mb-4">
    Total Weight of Wasted Food by Source
    </h2>
    <div style={{ width: '900px' }}>
      <Bar 
        data={processSourcesChartData()} 
        options={{
          responsive: true,
          plugins: {
            legend: { position: 'top' },
            tooltip: {
              enabled: true,
              callbacks: {
                label: function (tooltipItem) {
                  const dataset = tooltipItem.dataset;
                  const index = tooltipItem.dataIndex;
                  const source = sourcesData[index];

                  if (!source) return dataset.label + ": " + dataset.data[index];

                  return `${dataset.label}: ${source.total_waste_grams}g (${source.total_waste_kg}kg)`;
                }
              }
            },
          },
          scales: {
            x: { 
              title: { display: true, text: "Source of Food Waste" },
              ticks: { autoSkip: false } 
          
          },
            y: { title: { display: true, text: "Total Weight of Wasted Food (g)" } }
          }
        }} 
      />
    </div>
  </div>
)}

{foodTypesData && (
  <div className="flex flex-col items-center mb-12">
    <h2 className="text-xl font-semibold text-center text-gray-600 mb-4">
    Total Weight of Wasted Food by Type (g)
    </h2>
    <div style={{ width: '900px', height: `${foodTypesData.length * 60}px` }}> {/* Increased height */}
      <Bar 
        data={processFoodTypesChartData()} 
        options={{
          responsive: true,
          maintainAspectRatio: false, // Allows custom height
          indexAxis: 'y',
          plugins: {
            legend: { position: 'top' },
            tooltip: {
              enabled: true,
              callbacks: {
                label: function (tooltipItem) {
                  const dataset = tooltipItem.dataset;
                  const index = tooltipItem.dataIndex;
                  const food = foodTypesData[index];

                  if (!food) return dataset.label + ": " + dataset.data[index];

                  return `${dataset.label}: ${food.total_waste_grams}g (${food.total_waste_kg}kg)`;
                }
              }
            },
          },
          scales: {
            x: { title: { display: true, text: "Food Waste (g)" } },
            y: {
              title: { display: true, text: "Food Type" },
              ticks: { autoSkip: false } // Ensures all labels are displayed
            }
          }
        }} 
      />
    </div>
  </div>
)}


{reasonsData && (
  <div className="flex flex-col items-center mb-12">
    <h2 className="text-xl font-semibold text-center text-gray-600 mb-4">
    Total Food Waste by Source and Reason (g)
    </h2>
    <div style={{ width: '900px', height: '500px' }}>
      <Bar 
        data={processInteractionChartData()} 
        options={{
          responsive: true,
          maintainAspectRatio: false, 
          indexAxis: 'x', 
          plugins: {
            legend: { position: 'top' }, 
            tooltip: {
              enabled: true,
              callbacks: {
                label: function (tooltipItem) {
                  const dataset = tooltipItem.dataset;
                  const index = tooltipItem.dataIndex;
                  return `${dataset.label}: ${dataset.data[index]}g`;
                }
              }
            },
          },
          scales: {
            x: {
              title: { display: true, text: "" },
              ticks: { autoSkip: false },
              stacked: true, // ✅ Enable stacked bars
            },
            y: {
              title: { display: true, text: "" },
              stacked: true, // ✅ Enable stacked bars
            }
          }
        }} 
      />
    </div>
  </div>
)}


{foodTypeCostLoss && foodTypeCostLoss.length > 0 && (
  <div className="flex flex-col items-center mb-12">
    <h2 className="text-xl font-semibold text-center text-gray-600 mb-4">
    Cost Loss Analysis
    </h2>
    <div style={{ width: '900px', height: `${foodTypeCostLoss.length * 20}px` }}>
      <Bar 
        data={processFoodTypeCostLossChartData()} 
        options={{
          responsive: true,
          maintainAspectRatio: false,
          indexAxis: 'y', // Horizontal bar chart
          plugins: {
            legend: { position: 'top' },
            tooltip: {
              callbacks: {
                label: function (tooltipItem) {
                  const dataset = tooltipItem.dataset;
                  const index = tooltipItem.dataIndex;
                  return `${dataset.label}: ${dataset.data[index]} JOD`; // ✅ Change "$" to "JOD"
                }
              }
            },
          },
          scales: {
            x: { 
              title: { display: true, text: "" }, // ✅ Change "$" to "JOD"
              ticks: { autoSkip: false }
            },
            y: {
              title: { display: true, text: "" },
              ticks: { autoSkip: false }
            }
          }
        }} 
      />
    </div>
  </div>
)}


    </div>
  );
};

export default Home;
